import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton, TextField, Avatar,Divider, CircularProgress, Button } from '@mui/material';
import { Send, CloseOutlined, Chat, CheckCircleOutlineOutlined, Circle, RadioButtonUnchecked, Download, Refresh } from '@mui/icons-material';
import dayjs from 'dayjs';
import axios from 'axios';
import { formatDateTime } from '../../../utils/dateUtils';
import Loading from '../../../components/Loader/Loading';
import PdfIcon from '../../../assets/images/pdf-icon-4.png';
import ExcelIcon from '../../../assets/images/excel_icon.png'
import FileIcon from '../../../assets/images/file-icon.png'

const ChatBox = ({ onClose, bookingDetails,setBadgeContent,messages,isChatLoading,fetchMessages }) => {
  
  
  const chatEndRef = useRef(null);

  // Simulated data - replace with actual API calls
  const currentUser = {
    id: 'customer',
    name: bookingDetails?.add_customerFullName || 'Customer',
    role: 'customer'
  };

  const expert = {
    id: 'expert',
    name: bookingDetails?.providerName || 'Expert',
    role: 'expert'
  };

  
  // Scroll to bottom when messages update
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const groupMessagesByDate = (messages) => {
    const groups = {};
    const today = dayjs().startOf('day');
    const yesterday = today.subtract(1, 'day');
  
    messages.forEach(message => {
      const messageDate = dayjs(message.timestamp).startOf('day');
      let dateKey;
  
      if (messageDate.isSame(today)) {
        dateKey = 'Today';
      } else if (messageDate.isSame(yesterday)) {
        dateKey = 'Yesterday';
      } else {
        dateKey = dayjs(message.timestamp).format('MMM DD, YYYY');
      }
  
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(message);
    });
  
    // Sort messages within each group in ascending order
    Object.keys(groups).forEach(date => {
      groups[date].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    });
  
    // Sort the date groups in ascending order (oldest date at the top, newest at the bottom)
    const sortedGroups = Object.entries(groups)
      .sort((a, b) => {
        if (a[0] === 'Today') return 1;
        if (b[0] === 'Today') return -1;
        if (a[0] === 'Yesterday') return 1;
        if (b[0] === 'Yesterday') return -1;
        return new Date(a[0]) - new Date(b[0]); // Ascending order
      })
      .reduce((acc, [date, messages]) => {
        acc[date] = messages;
        return acc;
      }, {});
  
    return sortedGroups;
  };

  const MessageBubble = ({ message }) => {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: message.receiverUserType == 'PROVIDER' ? 'flex-end' : 'flex-start',
          mb: 1,
          gap: 1,
          position: 'relative'
        }}
      >
        <Box sx={{
          width: '75%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: message.receiverUserType == 'PROVIDER' ? 'flex-end' : 'flex-start'
        }}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            
              <Avatar sx={{ width: '.75rem', height: '.75rem', bgcolor:`${message.receiverUserType == 'PROVIDER' ?'primary.main':'secondary.main'}` , fontSize: '0.5rem' }}>
                {message.receiverUserType == 'PROVIDER'?'C':'E'}
              </Avatar>
          
            <Typography variant="caption" color="text.secondary" sx={{fontSize:'0.65rem'}}>
              {message.receiverUserType == 'PROVIDER' ? currentUser.name : expert.name}
            </Typography>
            {/* {message.receiverUserType == 'CUSTOMER' &&
              <Avatar sx={{ width: '.75rem', height: '.75rem', bgcolor: 'secondary.main', fontSize: '0.5rem' }}>
                C
              </Avatar>
            } */}
          </Box>
          {message.mssgType == 'text' && 
            <Box sx={{
              bgcolor: message.receiverUserType == 'PROVIDER' ? 'primary.main' : 'grey.100',
              color: message.receiverUserType == 'PROVIDER' ? 'white' : 'text.primary',
              borderRadius: 2,
              px: 2,
              py: 1,
              wordBreak: 'break-word',
            }}>
              <Typography sx={{fontSize:'0.65rem'}}>{message.content}</Typography>
            </Box>
          }
          {message.mssgType == 'image' && 
            <Box sx={{
              bgcolor: message.receiverUserType == 'PROVIDER' ? 'primary.main' : 'grey.100',
              color: message.receiverUserType == 'PROVIDER' ? 'white' : 'text.primary',
              borderRadius: 2,
              px: 2,
              py: 1,
              wordBreak: 'break-word',
            }}>
              {message.fileType== 'image' && (
                <img
                  src={message.fileName}
                  alt={message.fileName}
                  style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px' }}
                />
              )}
              {message.fileType== 'video' && (
                <video
                  controls
                  preload="none"
                  style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px' }}
                >
                  <source src={message.fileName} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )}
              {message.fileType == 'application' && (
                // {message.fileName && (
                  <a
                    href={message.fileName}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      textDecoration: 'none',
                      color: message.receiverUserType === 'PROVIDER' ? '#fff' : '#1976d2',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}
                  >
                    {message.fileName.endsWith('.pdf') ? (
                      <img src={PdfIcon} alt="PDF" width="20" height="20" />
                    ) : message.fileName.endsWith('.xlsx') || message.fileName.endsWith('.xls') ? (
                      <img src={ExcelIcon} alt="Excel" width="20" height="20" />
                    ) : (
                      <img src={FileIcon} alt="File" width="20" height="20" />
                    )}
                    <Typography sx={{ fontSize: '0.75rem' }}><Download/></Typography>
                  </a>
                // )}
              )}
            </Box>
          }
          <Box sx={{
            display: 'flex', 
            alignItems: 'center',
            justifyContent:'flex-end', 
            gap: 0.5,
            // width:'100%'
          }}>
            <Typography color="text.secondary" sx={{fontSize:'0.5rem', ml: 1}}>
              {formatDateTime(message.timestamp,'time')}
            </Typography>
            {message.read ? (
              <CheckCircleOutlineOutlined 
                sx={{ 
                  fontSize: '0.6rem', 
                  color: 'success.main' 
                }} 
              />
            ) : (
              <RadioButtonUnchecked
                sx={{ 
                  fontSize: '0.6rem', 
                  color: '#1565c0' 
                }} 
              />
            )}
          </Box>
        </Box>
      </Box>
    );
  };
  const DateDivider = ({ date }) => (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      my: 2,
      position: 'relative'
    }}>
      <Divider sx={{ flex: 1 }} />
      <Typography 
        variant="caption" 
        sx={{ 
          px: 2, 
          py: 0.5, 
          bgcolor: 'grey.100', 
          borderRadius: 1,
          fontSize: '0.65rem',
          color: 'text.secondary'
        }}
      >
        {date}
      </Typography>
      <Divider sx={{ flex: 1 }} />
    </Box>
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{
        borderBottom: '1px solid lightGray',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: '1% 2%',
        height: '8%',
        // bgcolor: '#5248db',
        bgcolor:'#3a2bde'
        // bgcolor:'#6256e6'
      }}>
        <Box sx={{display:'flex',alignItems:'center',gap:1}}>
          <Chat sx={{fontSize:'1rem',color:'#e8dfdfde'}} color='#fff'/>
          <Typography sx={{fontSize:'0.75rem',fontWeight:600,color:'#fff' }}> Cudel Chat</Typography>
        </Box>
        <Box sx={{display:'flex',gap:2}}>
          <IconButton sx={{p:0,color:'#e8dfdfde'}} onClick={fetchMessages} size='small'><Refresh fontSize='0.85rem'/></IconButton>
          <IconButton  conButton sx={{p:0,color:'#e8dfdfde'}} onClick={onClose} size='small'><CloseOutlined fontSize='0.85rem'/></IconButton>
        </Box>
      </Box>
      
      <Box sx={{
        height: '90%',
        overflowY: 'auto',
        bgcolor: 'background.default',
        p: '1% 2%',
      }}>
        {isChatLoading && 
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress/>
          </Box>
        }
        {messages.length === 0 && !isChatLoading ?
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Box sx={{textAlign:'center'}}>
              <Typography>No Chat History</Typography>
              {/* <Button size='small' variant='contained' onClick={()=>showSampleChat()}>Show Sample Chat</Button> */}
            </Box>
          </Box>
          :
        
          Object.entries(groupMessagesByDate(messages)).map(([date, dateMessages]) => (
            <Box key={date}>
              <DateDivider date={date} />
              {dateMessages.map((message) => (
                <MessageBubble key={message.id} message={message} />
              ))}
            </Box>
          ))
        }
        
        <div ref={chatEndRef} />
      </Box>
      
    </Box>
  );
};

export default ChatBox;